import {
  Typography,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useParams } from "react-router-dom";
import Picker from "../features/cfbd/picker/Picker";
import Scoreboard from "../features/cfbd/scoreboard/Scoreboard";
import Admin from "../league/components/Admin";
import { useAuth0 } from "@auth0/auth0-react";
import Members from "../league/components/Members";
import Rules from "../league/components/Rules";
import { useLeague } from "../hooks/useLeague/useLeague";
// Icons
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import { League as LeagueType } from "../types/league";

const League = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [data, setData] = useState<LeagueType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { getLeague } = useLeague();
  const { user } = useAuth0();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    const fetchLeague = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      const response = await getLeague(id);
      if (response instanceof Error) {
        setError(response.message);
      } else {
        setData(response);
      }
      setLoading(false);
    };
    if (id) fetchLeague();
  }, [id, getLeague]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  if (!data)
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No league data found.</Typography>
      </Box>
    );

  const isAdmin = data.admins.some((admin) => admin.email === user?.email);

  const navigationItems = [
    { label: "My Picks", icon: <SportsEsportsIcon />, index: 0 },
    { label: "Scoreboard", icon: <LeaderboardIcon />, index: 1 },
    ...(isAdmin
      ? [{ label: "Admin", icon: <AdminPanelSettingsIcon />, index: 2 }]
      : []),
    { label: "Members", icon: <PeopleIcon />, index: isAdmin ? 3 : 2 },
    { label: "Rules", icon: <SettingsIcon />, index: isAdmin ? 4 : 3 },
  ];

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Picker
            leagueId={data.id}
            userId={user?.sub}
            rules={data.rules}
            userEmail={user?.email}
          />
        );
      case 1:
        return (
          <Scoreboard
            leagueId={data.id}
            rules={data.rules}
            userEmail={user?.email}
          />
        );
      case 2:
        return isAdmin ? (
          <Admin
            rules={data.rules}
            leagueId={data.id}
            userEmail={user?.email}
          />
        ) : (
          <Members
            owner={data.owner}
            admins={data.admins}
            members={data.members}
          />
        );
      case 3:
        return isAdmin ? (
          <Members
            owner={data.owner}
            admins={data.admins}
            members={data.members}
          />
        ) : (
          <Rules rules={data.rules} />
        );
      case 4:
        return <Rules rules={data.rules} />;
      default:
        return (
          <Picker
            leagueId={data.id}
            userId={user?.sub}
            rules={data.rules}
            userEmail={user?.email}
          />
        );
    }
  };

  return (
    <HelmetProvider>
      <Helmet defer={false}>
        <title>{data.name}</title>
      </Helmet>

      {/* Desktop Layout */}
      {isDesktop ? (
        <Box sx={{ display: "flex", height: "calc(100vh - 128px)" }}>
          {/* Side Navigation */}
          <Drawer
            variant="permanent"
            sx={{
              width: 280,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: 280,
                boxSizing: "border-box",
                height: "100%",
                overflow: "auto",
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                pt: "64px",
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                {data.name}
              </Typography>
            </Box>
            <Divider />
            <List sx={{ pt: 1 }}>
              {navigationItems.map((item) => (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    selected={activeTab === item.index}
                    onClick={() => handleTabChange(item.index)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      mb: 0.5,
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color:
                          activeTab === item.index ? "inherit" : "text.primary",
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight:
                          activeTab === item.index ? "bold" : "normal",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Drawer>

          {/* Main Content */}
          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            {renderContent()}
          </Box>
        </Box>
      ) : (
        /* Mobile Layout */
        <Box sx={{ pb: 7, pt: 2 }}>
          {/* Mobile Header - League Name */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {data.name}
            </Typography>
          </Box>

          {/* Mobile Content */}
          <Box
            sx={{
              p: 2,
              // Ensure content isn't obscured by the bottom nav or iOS home indicator
              pb: "calc(80px + env(safe-area-inset-bottom, 12px))",
            }}
          >
            {renderContent()}
          </Box>

          {/* Mobile Bottom Navigation */}
          <Paper
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: theme.zIndex.appBar,
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              // Lift the bar above the iOS home indicator and increase tap target
              paddingBottom: "env(safe-area-inset-bottom, 8px)",
            }}
            elevation={3}
          >
            <BottomNavigation
              value={activeTab}
              onChange={(event, newValue) => handleTabChange(newValue)}
              showLabels
              sx={{
                "& .MuiBottomNavigationAction-root": {
                  minWidth: "auto",
                  padding: "6px 12px 8px",
                },
                "& .MuiBottomNavigationAction-label": {
                  fontSize: "0.75rem",
                },
              }}
            >
              {navigationItems.map((item) => (
                <BottomNavigationAction
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  value={item.index}
                />
              ))}
            </BottomNavigation>
          </Paper>

          {/* Mobile Drawer for additional options */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            variant="temporary"
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              "& .MuiDrawer-paper": {
                width: 280,
                boxSizing: "border-box",
                mt: "128px", // Account for both app bars
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                {data.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                League Options
              </Typography>
            </Box>
            <Divider />
            <List>
              {navigationItems.map((item) => (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    selected={activeTab === item.index}
                    onClick={() => {
                      handleTabChange(item.index);
                      setDrawerOpen(false);
                    }}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Drawer>
        </Box>
      )}
    </HelmetProvider>
  );
};

export default League;
